import { useEffect, useState } from "react";
import { ArrowRight, Loader2 } from "lucide-react";
import bizApi from "@/lib/businessApi";
import { useBiz } from "@/lib/businessI18n";
import { Badge, btnPrimary, cardCls, formatChf, inputCls } from "./ui";
import { DateField, TimeField } from "./fields";
import AddressAutocomplete from "@/components/AddressAutocomplete";

export default function Overview({ onNavigate, onOpenMission, canCreate = true }) {
  const { t, lang } = useBiz();
  const [data, setData] = useState(null);
  const [quick, setQuick] = useState({ pickup: "", dropoff: "", date: "", time: "", guest: "" });
  const [guests, setGuests] = useState([]);
  const [guestFocus, setGuestFocus] = useState(false);

  useEffect(() => {
    bizApi.get("/hub/overview").then((r) => setData(r.data)).catch(() => setData({}));
    bizApi.get("/guests").then((r) => setGuests(r.data || [])).catch(() => {});
  }, []);

  const guestMatches = quick.guest.trim()
    ? guests
        .filter((g) => `${g.first_name || ""} ${g.last_name || ""} ${g.company || ""}`.toLowerCase().includes(quick.guest.trim().toLowerCase()))
        .sort((a, b) => (a.first_name || "").localeCompare(b.first_name || "", "fr"))
        .slice(0, 6)
    : [];

  const goBuilder = () => onNavigate("new_mission", {
    guest: quick.guest ? { first_name: quick.guest, last_name: "" } : null,
    duplicate: quick.pickup ? {
      type: "transfer",
      legs: [{ pickup: quick.pickup, dropoff: quick.dropoff, date: quick.date, time: quick.time, stops: [] }],
      guests: quick.guest ? [{ first_name: quick.guest, last_name: "", phone: "", email: "" }] : [],
    } : null,
  });

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-miles" /></div>;

  const today = data.today || {};
  const kpis = [
    [t("overview.arrivals"), today.arrivals ?? 0],
    [t("overview.departures"), today.departures ?? 0],
    [t("overview.active"), today.active ?? 0],
    [t("overview.completed"), today.completed ?? 0],
  ];

  return (
    <div data-testid="hub-overview">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-ink">{t("overview.welcome")}</h1>
          <p className="mt-0.5 text-sm text-slate">{t("overview.today")} · {new Date().toLocaleDateString(lang === "de" ? "de-CH" : lang === "en" ? "en-GB" : "fr-CH", { day: "numeric", month: "long", year: "numeric" })}</p>
        </div>
        <div className="text-right text-xs text-slate">
          <p>{t("overview.spend_month")} : <span className="font-mono font-semibold text-ink">{formatChf(data.spend_month)}</span></p>
          <p className="mt-0.5">{t("overview.missions_month")} : <span className="font-semibold text-ink">{data.missions_month ?? 0}</span></p>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="overview-kpis">
        {kpis.map(([label, value]) => (
          <div key={label} className={`${cardCls} p-4`}>
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate/70">{label}</p>
            <p className="mt-1 text-2xl font-bold text-ink">{value}</p>
          </div>
        ))}
      </div>

      {/* actions requises */}
      <section className={`${cardCls} mt-4 p-5`} data-testid="action-required">
        <h2 className="text-sm font-bold text-ink">{t("overview.action_required")}</h2>
        {(data.action_required || []).length === 0 ? (
          <p className="mt-2 text-xs text-slate">{t("overview.none")}</p>
        ) : (
          <div className="mt-2 space-y-2">
            {data.action_required.map((a, i) => (
              <button key={i} type="button" onClick={() => a.mission_id && onOpenMission(a.mission_id)}
                className="flex w-full items-center gap-3 rounded-xl border border-ink/[0.06] p-3 text-left hover:border-miles/30"
                data-testid={`action-${i}`}>
                <span className={`h-2 w-2 shrink-0 rounded-full ${a.severity === "warning" ? "bg-amber-500" : "bg-miles"}`} />
                <span className="flex-1 text-sm font-medium text-ink">{a.message}</span>
                <span className="text-xs text-slate">{a.detail}</span>
              </button>
            ))}
          </div>
        )}
      </section>

      <div className={`mt-4 grid gap-4 ${canCreate ? "lg:grid-cols-[1fr_320px]" : ""}`}>
        {/* timeline */}
        <section className={`${cardCls} p-5`} data-testid="overview-timeline">
          <h2 className="text-sm font-bold text-ink">{t("overview.timeline")}</h2>
          {(data.timeline || []).length === 0 ? (
            <p className="mt-2 text-xs text-slate">{t("overview.no_missions")}</p>
          ) : (
            <ol className="mt-3 space-y-0">
              {data.timeline.map((e, i) => (
                <li key={i}>
                  <button type="button" onClick={() => onOpenMission(e.mission.id)}
                    className="flex w-full items-center gap-3 border-l-2 border-ink/[0.08] py-2.5 pl-4 text-left transition-colors hover:border-miles"
                    data-testid={`timeline-${i}`}>
                    <span className="w-12 font-mono text-xs font-bold text-ink">{e.time || "—"}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-ink">{e.mission.guest_name || t(`type.${e.mission.type}`)}</span>
                      <span className="block truncate text-xs text-slate">{e.mission.pickup} → {e.mission.dropoff || "—"}</span>
                    </span>
                    {e.mission.flight?.number && <span className="text-[10px] font-medium text-miles">✈ {e.mission.flight.number}</span>}
                    <Badge tone={e.mission.status}>{t(`status.${e.mission.status}`)}</Badge>
                  </button>
                </li>
              ))}
            </ol>
          )}
        </section>

        {/* quick booking */}
        {canCreate && (
        <section className={`${cardCls} h-fit p-5`} data-testid="quick-booking">
          <h2 className="text-sm font-bold text-ink">{t("overview.quick")}</h2>
          <p className="mt-1 text-[11px] text-slate">{t("overview.quick_hint")}</p>
          <div className="mt-3 space-y-2">
            <AddressAutocomplete value={quick.pickup} testId="quick-pickup" inputClass={inputCls}
              placeholder={t("overview.pickup")} onChange={(v) => setQuick({ ...quick, pickup: v })} />
            <AddressAutocomplete value={quick.dropoff} testId="quick-dropoff" inputClass={inputCls}
              placeholder={t("overview.destination")} onChange={(v) => setQuick({ ...quick, dropoff: v })} />
            <div className="grid grid-cols-2 gap-2">
              <DateField value={quick.date} testId="quick-date"
                onChange={(v) => setQuick({ ...quick, date: v })} />
              <TimeField value={quick.time} testId="quick-time"
                onChange={(v) => setQuick({ ...quick, time: v })} />
            </div>
            <div className="relative">
              <input className={inputCls} placeholder={t("overview.guest")} value={quick.guest}
                onChange={(e) => setQuick({ ...quick, guest: e.target.value })}
                onFocus={() => setGuestFocus(true)} onBlur={() => setTimeout(() => setGuestFocus(false), 150)}
                data-testid="quick-guest" />
              {guestFocus && guestMatches.length > 0 && (
                <div className="absolute left-0 right-0 z-[80] mt-1 overflow-hidden rounded-xl border border-ink/10 bg-white shadow-lg" data-testid="quick-guest-results">
                  {guestMatches.map((g) => (
                    <button key={g.id} type="button" data-testid={`quick-guest-option-${g.id}`}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => { setQuick({ ...quick, guest: `${g.first_name || ""} ${g.last_name || ""}`.trim() }); setGuestFocus(false); }}
                      className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-cloud">
                      <span className="font-medium text-ink">{g.first_name} {g.last_name}</span>
                      <span className="truncate text-xs text-slate">{g.company || g.phone || ""}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button type="button" onClick={goBuilder} disabled={!quick.pickup}
              className={`${btnPrimary} w-full`} data-testid="quick-continue">
              {t("overview.continue")} <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </section>
        )}
      </div>
    </div>
  );
}
